import React from "react";
import { Alert, Divider, Tag, Table, Empty } from "antd";
import { ToolOutlined } from "@ant-design/icons";
import ModalItem from "./ModalItem";
import ButtonItem from "./ButtonItem";
import dineroDisabledMask from "./dineroDisabledMask";

/*
  Por que no cuadra esta factura contra su XML.

  Es presentacional puro: no consulta nada, solo pinta el objeto que devuelve
  `Utils/diagnosticoCuadreFactura.diagnosticarCuadreFactura`. Lo usan las seis pantallas de
  captura de factura de proveedor (sin OC, con OC, caja chica, recibir factura multiple, nota de
  credito de compra y devolucion de compra).

  Las tablas son el `Table` de antd y no `TableItem` a proposito: TableItem hardcodea
  `pagination` y `scroll={{ y: '60vh' }}`, que dentro de un modal deja un paginador y una barra de
  scroll sobre tablas de cuatro renglones.

  `onAplicarAjuste` es opcional: solo se pinta el boton cuando la diferencia cabe en el limite de
  ajuste configurado para la empresa.
*/

const COLOR_SEVERIDAD = {
  alta: "error",
  media: "warning",
  info: "info",
};

const fila = (etiqueta, valor, color) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "6px 0",
    }}
  >
    <span style={{ fontSize: 13, color: "#8c8c8c" }}>{etiqueta}</span>
    <span style={{ fontSize: 15, fontWeight: 700, color: color || "#262626" }}>{valor}</span>
  </div>
);

const columnasResumen = [
  { title: "Concepto", dataIndex: "concepto", key: "concepto" },
  {
    title: "XML",
    dataIndex: "xml",
    key: "xml",
    align: "right",
    render: (value) => dineroDisabledMask(value),
  },
  {
    title: "Sistema",
    dataIndex: "sistema",
    key: "sistema",
    align: "right",
    render: (value) => dineroDisabledMask(value),
  },
  {
    title: "Diferencia",
    dataIndex: "diferencia",
    key: "diferencia",
    align: "right",
    render: (value) => (
      <span
        style={{ color: value === 0 ? "#8c8c8c" : "#d4380d", fontWeight: value === 0 ? 400 : 700 }}
      >
        {dineroDisabledMask(value)}
      </span>
    ),
  },
];

// Los hallazgos de renglon traen dos formas: importes (se pinta dinero) o firmas fiscales
// (se pinta el texto de los impuestos de cada lado).
const columnasRenglones = (esTexto) => {
  const celda = (value) =>
    esTexto ? value : value === null || value === undefined ? "—" : dineroDisabledMask(value);
  return [
    { title: "#", dataIndex: "renglon", key: "renglon", width: 45 },
    { title: "Insumo", dataIndex: "insumo", key: "insumo", ellipsis: true },
    {
      title: "Sistema",
      dataIndex: "sistema",
      key: "sistema",
      align: esTexto ? "left" : "right",
      render: celda,
    },
    { title: "XML", dataIndex: "xml", key: "xml", align: esTexto ? "left" : "right", render: celda },
    {
      title: "Diferencia",
      dataIndex: "diferencia",
      key: "diferencia",
      align: "right",
      render: (value) => dineroDisabledMask(value),
    },
    { title: "Nota", dataIndex: "nota", key: "nota", ellipsis: true },
  ];
};

const ModalDiagnosticoCuadre = ({ visible, onCancel, diagnostico, onAplicarAjuste }) => {
  const cuadra = diagnostico?.cuadra;
  const colorDiferencia = cuadra ? "#008f2d" : diagnostico?.ajusteCubre ? "#d48806" : "#d4380d";

  return (
    <ModalItem
      title={cuadra ? "Comparación contra el XML" : "¿Por qué no cuadra?"}
      visible={visible}
      onCancel={onCancel}
      width={900}
      spinning={false}
      paddingTop
    >
      {!diagnostico ? (
        <Empty description="Carga un XML para poder comparar" />
      ) : (
        <>
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: "4px 12px",
              marginBottom: 16,
            }}
          >
            {fila("Total a pagar del XML", dineroDisabledMask(diagnostico.totalXml))}
            <Divider style={{ margin: 0 }} />
            {fila("Total a pagar capturado", dineroDisabledMask(diagnostico.totalCapturado))}
            <Divider style={{ margin: 0 }} />
            {fila("Diferencia", dineroDisabledMask(diagnostico.diferencia), colorDiferencia)}
          </div>

          {cuadra && (
            <Alert
              type="success"
              showIcon
              message="Los totales cuadran con el XML"
              description={
                diagnostico.hallazgos.length
                  ? "Aun así hay detalles que conviene revisar antes de guardar."
                  : "El subtotal, los impuestos y las retenciones coinciden con el CFDI."
              }
              style={{ marginBottom: 16 }}
            />
          )}

          <Table
            size="small"
            bordered
            pagination={false}
            rowKey="concepto"
            dataSource={diagnostico.resumen}
            columns={columnasResumen}
            style={{ marginBottom: 16 }}
          />

          {diagnostico.hallazgos.map((valueHallazgo, index) => (
            <Alert
              key={valueHallazgo.tipo + index}
              type={COLOR_SEVERIDAD[valueHallazgo.severidad] || "info"}
              showIcon
              style={{ marginBottom: 10 }}
              message={
                <span>
                  {valueHallazgo.titulo}
                  {valueHallazgo.monto !== undefined && valueHallazgo.monto !== 0 && (
                    <Tag style={{ marginLeft: 8 }}>{dineroDisabledMask(valueHallazgo.monto)}</Tag>
                  )}
                </span>
              }
              description={
                <>
                  {valueHallazgo.detalle}
                  {valueHallazgo.renglones && valueHallazgo.renglones.length > 0 && (
                    <Table
                      size="small"
                      bordered
                      pagination={false}
                      rowKey="renglon"
                      dataSource={valueHallazgo.renglones}
                      columns={columnasRenglones(valueHallazgo.columnas === "texto")}
                      style={{ marginTop: 10, backgroundColor: "#fff" }}
                    />
                  )}
                </>
              }
            />
          ))}

          {!cuadra && diagnostico.hallazgos.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="No se encontró la causa"
              description="Los totales difieren pero el desglose del XML coincide con lo capturado. Revisa el PDF del proveedor contra el comprobante."
            />
          )}

          {onAplicarAjuste && diagnostico.ajusteCubre && (
            <div style={{ textAlign: "right", marginTop: 16 }}>
              <ButtonItem ambar icon={<ToolOutlined />} onClick={onAplicarAjuste}>
                Aplicar ajuste automático
              </ButtonItem>
            </div>
          )}
        </>
      )}
    </ModalItem>
  );
};

export default ModalDiagnosticoCuadre;
